import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Textarea, Select } from "@legal-500/legal500-design-system";
import { useEffect } from "react";
import { useForm, Controller, type Resolver } from "react-hook-form";
import type { CrmPackageMapping } from "../../api/crm-package-mappings.service.js";
import { SearchableSelect } from "../Form/SearchableSelect/SearchableSelect.js";
import { usePackageOptions } from "../../pages/Subscriptions/hooks/usePackageOptions.js";
import {
  CrmPackageMappingFormSchema,
  type CrmPackageMappingFormData,
} from "../../schemas/crmPackageMappingSchema.js";
import styles from "./CrmPackageMappingForm.module.css";

export type CrmPackageMappingFormProps = {
  initialValues?: Partial<CrmPackageMapping>;
  onSubmit: (values: CrmPackageMappingFormData) => void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
  mode: "create" | "edit";
  onDirtyChange?: (isDirty: boolean) => void;
};

export function CrmPackageMappingForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting = false,
  mode,
  onDirtyChange,
}: CrmPackageMappingFormProps) {
  const { packageOptions, isLoading: loadingPackages, error: packagesError } = usePackageOptions();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    watch,
  } = useForm<CrmPackageMappingFormData>({
    resolver: zodResolver(CrmPackageMappingFormSchema) as Resolver<CrmPackageMappingFormData>,
    defaultValues: {
      crmPackageId: initialValues?.crmPackageId || "",
      action: initialValues?.action || "map",
      packageId: initialValues?.packageId || "",
      description: initialValues?.description || "",
    },
  });

  const action = watch("action");

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <section className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Mapping Details</h3>

        <div className={styles.detailsGrid}>
          <div className={styles.detailsLeftColumn}>
            <div className={styles.formGroup}>
              <Input
                {...register("crmPackageId")}
                label="CRM Package ID"
                placeholder="e.g., 73f5d6b0-9b34-f011-8c4c-0022481ac898"
                error={errors.crmPackageId?.message}
                helperText="The unique identifier from the CRM system"
                fullWidth
                required
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <Controller
                name="action"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Action"
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    options={[
                      { value: "map", label: "Map to Package" },
                      { value: "skip", label: "Skip (ignore webhooks)" },
                    ]}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            {action === "map" && (
              <div className={styles.formGroup}>
                <Controller
                  name="packageId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SearchableSelect
                      name="packageId"
                      label="Package"
                      required
                      value={field.value || ""}
                      options={packageOptions}
                      onChange={value => field.onChange(value ?? "")}
                      disabled={isSubmitting || loadingPackages}
                      placeholder="Select a package..."
                      helperText="The fulfilment package to map this CRM package to"
                      error={fieldState.error?.message || packagesError?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>

          <div className={styles.detailsRightColumn}>
            <div className={styles.formGroup}>
              <Textarea
                {...register("description")}
                label="Description"
                placeholder="Enter a description for this mapping"
                error={errors.description?.message}
                helperText="Optional description to help identify this mapping"
                fullWidth
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      </section>

      <div className={styles.formActions}>
        <Button variant="outlined" color="default" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          className={styles.submitButton}
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
